import React, { useState, useEffect } from 'react';
import { transactionsAPI } from '../services/api';

const statusClasses = {
  active: 'bg-blue-100 text-blue-700',
  returned: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
};
const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusClasses[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>
);

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchTransactions(); }, []);
  useEffect(() => { filterTransactions(); }, [transactions, statusFilter, searchTerm]);

  const fetchTransactions = async () => {
    try {
      const response = await transactionsAPI.getAll();
      setTransactions(response.data.transactions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let result = [...transactions];
    if (statusFilter !== 'all') result = result.filter(t => t.status === statusFilter);
    if (searchTerm) result = result.filter(t =>
      t.tool_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFiltered(result);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-sm text-slate-500">Loading transactions...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search by tool or employee name..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="returned">Returned</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Transaction History</h3>
          <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">{filtered.length}</span>
        </div>
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-slate-500 font-medium">No transactions found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Tool', 'Employee', 'Checkout Date', 'Expected Return', 'Actual Return', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(tx => (
                  <tr key={tx.transaction_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 text-sm">{tx.tool_name}</p>
                      <p className="text-xs text-slate-500">{tx.serial_number}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{tx.first_name} {tx.last_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{new Date(tx.checkout_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{new Date(tx.expected_return_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{tx.actual_return_date ? new Date(tx.actual_return_date).toLocaleDateString() : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><Badge status={tx.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
